import { Box, Grid, makeStyles, MenuItem, Typography } from "@material-ui/core";
import { CloudUpload, ExpandLess, ExpandMore } from "@material-ui/icons";
import { FormikProvider, useFormik } from "formik";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomButton, { ButtonTypeEnum } from "src/components/CustomButton/custom-button.component";
import CustomTextField from "src/components/CustomTextField/custom-textfield.component";
import Form from "src/components/Form/Form";
import { GetRealSizeViewerFormInitialValues, GetRealSizeViewerFormValidationSchema, IRealSizeViewerFormFields } from "src/models";
import { SharedService } from "src/services/shared.service";
import { LengthUnitEnum as LengthUnitEnum, ShapeEnum, RefImageTypeEnum } from "src/shared/enum";

export const useStyles = makeStyles({
    cursorPointer: { 
        cursor: "pointer"
    },
    widthFull: {
        width: "100%"
    },
    marginTop18: {
        marginTop: "18px"
    },
    errorText: {
        color: "red"
    },
    imgUpload: {
        width: "100%",
        border: "1px dashed black",
        borderRadius: "4px",
        cursor: "pointer",
        paddingLeft: "8px",
        paddingBottom: "4px",
        paddingTop: "4px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center"
    },
    form: {
        minWidth: "100%",
        maxWidth: "100vh",
    }
})

export interface IRealSizeViewerFormProps {
    generateImage: Function;
}


const RealSizeViewerForm: React.FC<IRealSizeViewerFormProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [referenceImageType, setReferenceImageType] = useState<RefImageTypeEnum>(RefImageTypeEnum.Default);
    const [objectImageType, setObjectImageType] = useState<ShapeEnum>(ShapeEnum.Box);
    const [unitSelected, setUnitSelected] = useState<LengthUnitEnum>(LengthUnitEnum.Feet);
    const [refImgFormShown, setRefImgFormShown] = useState<boolean>(false);
    const [objImgFormShown, setObjImgFormShown] = useState<boolean>(true);

    const formik = useFormik<IRealSizeViewerFormFields>({
        initialValues: GetRealSizeViewerFormInitialValues(),
        validationSchema: GetRealSizeViewerFormValidationSchema(t, referenceImageType, objectImageType, unitSelected),
        onSubmit: (values) => props.generateImage(values)
    })

    //open file dialog
    const showFileDialog = useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>, isObjImage = false) => {
            e.preventDefault();

            new SharedService().showFileDialog(false, 'image/*', async files => {
                const [file] = files;
                if(isObjImage) {
                    formik.setFieldValue('objectImage', file);    
                } else {
                    formik.setFieldValue('referenceImage', file);
                }
            });
        },
        [formik]
    );

    return (
        <>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3} className={classes.form}>

                        <Grid container item>
                            <CustomTextField name={'unit'} label={t('label-unit')} placeholder={t('label-unit')} formik={formik} fullWidth select
                                children={Object.keys(LengthUnitEnum).map((unit, index) => (
                                    <MenuItem key={index} value={LengthUnitEnum[unit]}>{unit}</MenuItem>
                                ))} onChange={(value: LengthUnitEnum) => setUnitSelected(value)}
                            />
                        </Grid>

                        <Grid className={classes.cursorPointer} container item onClick={() => {
                            setObjImgFormShown((prev) => !prev);
                        }}>
                            <Box className={classes.widthFull} display="flex" justifyContent="space-between">
                                <Typography>{t('label-object-image')}</Typography>
                                {objImgFormShown ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                        </Grid>

                        {objImgFormShown && <><Grid container item>
                            <CustomTextField name={'shape'} label={t('label-shape')} placeholder={t('label-shape')} formik={formik} fullWidth select
                                children={Object.keys(ShapeEnum).filter(x => isNaN(Number(x))).map((type, index) => (
                                    <MenuItem key={index} value={ShapeEnum[type]}>{type}</MenuItem>
                                ))} onChange={(value: ShapeEnum) => {
                                    setObjectImageType(value)
                                    formik.setFieldValue('objectImage', null);
                                }}
                            />
                        </Grid>

                        {/* Show ref image fields when ref image type is custom */}
                        {formik.values.shape === ShapeEnum.Custom && <Grid item container>
                            <Box className={classes.imgUpload} onClick={(e) => showFileDialog(e, true)}>
                                <Box width="fit-content" textAlign="center">
                                    <Box>
                                        <CloudUpload />
                                    </Box>
                                    {t('label-upload')}
                                </Box>
                                {formik.values?.objectImage && <Box ml={1}>{formik.values.objectImage?.name}</Box>}
                                {formik.touched?.objectImage && formik.errors?.objectImage && <Box ml={1} className={classes.errorText}>{formik.errors.objectImage}</Box>}
                            </Box>
                        </Grid>}

                        <Grid container item>
                            <CustomTextField type="number" name={'height'} label={t('label-height')} placeholder={t('label-height')} formik={formik} fullWidth />
                        </Grid>

                        <Grid container item>
                            <CustomTextField type="number" name={'width'} label={t('label-width')} placeholder={t('label-width')} formik={formik} fullWidth />
                        </Grid>

                        <Grid container item>
                            <CustomTextField type="number" name={'length'} label={t('label-length')} disabled={objectImageType === ShapeEnum.Custom} placeholder={t('label-length')} formik={formik} fullWidth />
                        </Grid></>}

                        <Grid className={classes.cursorPointer} container item onClick={() => {
                            setRefImgFormShown((prev) => !prev);
                        }}>
                            <Box className={classes.widthFull} display="flex" justifyContent="space-between">
                                <Typography>{t('label-reference-image')}</Typography>
                                {refImgFormShown ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                        </Grid>

                        {refImgFormShown && <Grid container item>
                            <CustomTextField name={'referenceImageType'} label={t('label-reference-image-type')} placeholder={t('label-reference-image-type')} formik={formik} fullWidth select
                                children={Object.keys(RefImageTypeEnum).map((type, index) => (
                                    <MenuItem key={index} value={RefImageTypeEnum[type]}>{type}</MenuItem>
                                ))} onChange={(value: RefImageTypeEnum) => {
                                    setReferenceImageType(value);
                                    formik.setFieldValue('referenceImage', null);
                                }}
                            />

                            {/* Show ref image fields when ref image type is custom */}
                            {formik.values.referenceImageType === RefImageTypeEnum.Custom && <Grid container spacing={3}>
                                <Grid className={classes.marginTop18} item container justifyContent="space-between">
                                    <Box className={classes.imgUpload} onClick={showFileDialog}>
                                        <Box width="fit-content" textAlign="center">
                                            <Box>
                                                <CloudUpload />
                                            </Box>
                                            {t('label-upload')}
                                        </Box>
                                        {formik.values?.referenceImage && <Box ml={1}>{formik.values.referenceImage?.name}</Box>}
                                        {formik.touched?.referenceImage && formik.errors?.referenceImage && <Box ml={1} className={classes.errorText}>{formik.errors.referenceImage}</Box>}
                                    </Box>
                                </Grid>

                                <Grid item container>
                                    <CustomTextField type="number" name={'referenceImageHeight'} label={t('label-height')} placeholder={t('label-reference-image-height')} formik={formik} fullWidth />
                                </Grid>

                                <Grid item container>
                                    <CustomTextField type="number" name={'referenceImageWidth'} label={t('label-width')} placeholder={t('label-reference-image-width')} formik={formik} fullWidth />
                                </Grid>
                            </Grid>}
                        </Grid>}

                        {/* Row */}
                        <Grid container item justifyContent='center' spacing={2}>
                            <Grid item>
                                <CustomButton type={ButtonTypeEnum.Submit} buttonText={t('label-generate-image')} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Form>
            </FormikProvider>
        </>
    )
}

export default RealSizeViewerForm;